import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { Avatar, Username, VipBadge } from "./UserBadge";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CardWrap } from "./Sidebar";
import { 
  Users, Rocket, Quote, List, Star, Trophy, Clock, FileText, Send 
} from "lucide-react";

// --- Popular Contributors Widget ---
export function PopularContributorsWidget({ title }) {
  const [timeframe, setTimeframe] = useState("week");
  
  const { data } = useQuery({ 
    queryKey: ["popularContributors", timeframe], 
    queryFn: async () => (await api.get(`/idg/widgets/data/users/contributors?timeframe=${timeframe}&limit=5`)).data,
    staleTime: 30000 
  });
  
  const users = data || [];
  
  return (
    <CardWrap title={title || "Contribuitori Populari"} icon={Users} testid="widget-popular-contributors">
      <div className="flex bg-[#06080d] border border-[#141824] mb-3 p-1 overflow-x-auto text-[10px] tf-mono scrollbar-hide">
        {["week", "month", "year", "all"].map(tf => (
          <button 
            key={tf}
            onClick={() => setTimeframe(tf)}
            className={`flex-1 py-1 px-2 capitalize transition-colors ${timeframe === tf ? 'bg-[var(--tf-primary)] text-black font-bold' : 'text-zinc-500 hover:text-white'}`}
          >
            {tf === "all" ? "All Time" : tf}
          </button>
        ))}
      </div>
      
      <div className="space-y-1.5">
        {users.map((u, i) => (
          <div key={u.user?.id || i} className="flex items-center gap-2.5 bg-[#06080d] hover:bg-[#0c1018] p-2 border border-[#141824] hover:border-[var(--tf-primary)]/50 transition-all">
            <div className={`w-5 text-center font-extrabold text-xs font-mono ${i === 0 ? 'text-amber-400' : i === 1 ? 'text-zinc-300' : i === 2 ? 'text-amber-600' : 'text-zinc-600'}`}>
              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
            </div>
            <Link to={`/u/${u.user?.username}`} className="shrink-0">
              <Avatar user={u.user} size={34} />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Link to={`/u/${u.user?.username}`} className="truncate block font-bold text-xs hover:underline">
                  <Username user={u.user} />
                </Link>
                {((u.user?.loadout?.banner_badge && u.user?.loadout?.banner_badge !== "none") || (u.user?.vip_tier && u.user?.vip_tier !== "none")) && (
                  <VipBadge tier={u.user?.loadout?.banner_badge || u.user?.vip_tier} />
                )}
              </div>
              <div className="text-[10px] text-amber-400 font-bold mt-0.5 flex items-center gap-1 font-mono">
                <Trophy className="w-3 h-3 text-amber-400" /> {u.points} pct
              </div>
            </div>
          </div>
        ))}
        {users.length === 0 && <p className="text-xs text-zinc-500 font-mono text-center py-3">Nu există date recente.</p>}
      </div>
    </CardWrap>
  );
}


// --- Time Spent Online Widget ---
export function TimeOnlineWidget({ title }) {
  const [timeframe, setTimeframe] = useState("today");
  
  const { data } = useQuery({ 
    queryKey: ["timeOnline", timeframe], 
    queryFn: async () => (await api.get(`/idg/widgets/data/users/time-online?timeframe=${timeframe}&limit=5`)).data,
    staleTime: 30000 
  });
  
  const users = data || [];
  
  const formatTime = (secs) => {
    const s = Math.max(0, secs || 0);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${s}s`;
  };
  
  return (
    <CardWrap title={title || "Timp Online"} icon={Rocket} testid="widget-time-online">
      <div className="flex bg-[#06080d] border border-[#141824] mb-3 p-1 overflow-x-auto text-[10px] tf-mono scrollbar-hide">
        {["today", "week", "month", "year", "all"].map(tf => (
          <button 
            key={tf}
            onClick={() => setTimeframe(tf)}
            className={`flex-1 py-1 px-2 capitalize transition-colors ${timeframe === tf ? 'bg-[var(--tf-primary)] text-black font-bold' : 'text-zinc-500 hover:text-white'}`}
          >
            {tf}
          </button>
        ))}
      </div>
      
      <div className="space-y-1.5">
        {users.map((u, i) => (
          <div key={u.user?.id || i} className="flex items-center gap-2.5 bg-[#06080d] hover:bg-[#0c1018] p-2 border border-[#141824] hover:border-[var(--tf-primary)]/50 transition-all">
            <div className={`w-5 text-center font-extrabold text-xs font-mono ${i === 0 ? 'text-cyan-400' : i === 1 ? 'text-zinc-300' : i === 2 ? 'text-cyan-600' : 'text-zinc-600'}`}>
              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`}
            </div>
            <Link to={`/u/${u.user?.username}`} className="shrink-0">
              <Avatar user={u.user} size={34} />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Link to={`/u/${u.user?.username}`} className="truncate block font-bold text-xs hover:underline">
                  <Username user={u.user} />
                </Link>
                {((u.user?.loadout?.banner_badge && u.user?.loadout?.banner_badge !== "none") || (u.user?.vip_tier && u.user?.vip_tier !== "none")) && (
                  <VipBadge tier={u.user?.loadout?.banner_badge || u.user?.vip_tier} />
                )}
              </div>
              <div className="text-[10px] text-cyan-300 font-bold mt-0.5 flex items-center gap-1 font-mono">
                <Clock className="w-3 h-3 text-cyan-400" /> {formatTime(u.seconds)}
              </div>
            </div>
          </div>
        ))}
        {users.length === 0 && <p className="text-xs text-zinc-500 font-mono text-center py-3">Nu există date recente.</p>}
      </div>
    </CardWrap>
  );
}


// --- Recent Statuses Widget ---
export function RecentStatusesWidget({ title }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [content, setContent] = useState("");
  
  const { data: statuses = [] } = useQuery({ 
    queryKey: ["recentStatuses"], 
    queryFn: async () => (await api.get(`/idg/widgets/data/statuses/recent`)).data,
    staleTime: 15000 
  });
  
  const postMutation = useMutation({
    mutationFn: async (text) => await api.post(`/idg/widgets/data/statuses`, { content: text }),
    onSuccess: () => {
      setContent("");
      queryClient.invalidateQueries(["recentStatuses"]);
      toast.success("Statusul a fost publicat!");
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || "Eroare la publicarea statusului. Încearcă din nou.";
      toast.error(msg);
    }
  });
  
  const handlePost = (e) => {
    e.preventDefault();
    if (content.trim() && !postMutation.isPending) {
      postMutation.mutate(content.trim());
    }
  };
  
  return (
    <CardWrap title={title || "Statusuri Recente"} icon={Quote} testid="widget-recent-statuses">
      {user ? (
        <form onSubmit={handlePost} className="mb-3 relative">
          <input 
            type="text" 
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="La ce te gândești acum?..." 
            className="w-full bg-[#06080d] border border-[#141824] py-2 px-3 text-xs focus:border-[var(--tf-primary)] outline-none text-white pr-9 placeholder-zinc-500 font-mono transition-colors"
          />
          <button 
            type="submit" 
            disabled={!content.trim() || postMutation.isPending} 
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[var(--tf-primary)] disabled:opacity-40 transition-colors cursor-pointer"
            title="Publică status"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      ) : (
        <div className="mb-3 px-3 py-2 bg-[#06080d] border border-[#141824] text-[11px] text-zinc-400 text-center font-mono">
          <Link to="/login" className="text-[var(--tf-primary)] font-bold hover:underline">Autentifică-te</Link> pentru a posta un status.
        </div>
      )}
      
      <div className="space-y-2">
        {statuses.map(s => (
          <div key={s.id} className="bg-[#06080d] p-2.5 border border-[#141824] hover:border-[var(--tf-primary)]/50 transition-all">
            <div className="flex items-start gap-2.5">
              <Link to={`/u/${s.author?.username}`}>
                <Avatar user={s.author} size={32} />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Link to={`/u/${s.author?.username}`}>
                    <Username user={s.author} className="text-xs font-bold" />
                  </Link>
                  {((s.author?.loadout?.banner_badge && s.author?.loadout?.banner_badge !== "none") || (s.author?.vip_tier && s.author?.vip_tier !== "none")) && (
                    <VipBadge tier={s.author?.loadout?.banner_badge || s.author?.vip_tier} />
                  )}
                </div>
                <p className="text-xs text-zinc-200 mt-1 break-words leading-snug font-sans">{s.content}</p>
                <div className="flex items-center gap-2 mt-1.5 text-[10px] text-zinc-500 font-mono">
                  <span>{s.created_at ? new Date(s.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Acum"}</span>
                  <span>·</span>
                  <span>{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</span>
                  {s.replies?.length > 0 && (
                    <>
                      <span>·</span>
                      <span className="text-[var(--tf-primary)]">{s.replies.length} răspunsuri</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {statuses.length === 0 && (
          <p className="text-xs text-zinc-500 font-mono text-center py-4">Nu există statusuri recente.</p>
        )}
      </div>
    </CardWrap>
  );
}


// --- Important Topics Widget ---
export function ImportantTopicsWidget({ title }) {
  const { data: topics = [] } = useQuery({ 
    queryKey: ["importantTopics"], 
    queryFn: async () => (await api.get(`/idg/widgets/data/topics/important`)).data,
    staleTime: 120000 
  });
  
  return (
    <CardWrap title={title} icon={List} testid="widget-important-topics">
      <div className="space-y-1.5">
        {topics.map(t => (
          <Link key={t.id} to={`/topic/${t.id}`} className="flex items-center gap-3 bg-[#06080d] p-2 border border-[#141824] hover:border-[var(--tf-primary)]/50 transition-colors">
            <Avatar user={t.author} size={36} />
            <div className="flex-1 min-w-0">
              <h5 className="text-xs font-bold text-white truncate">{t.title}</h5>
              <div className="text-[10px] text-zinc-500 flex items-center gap-1 mt-0.5 truncate">
                <Username user={t.author} asText /> &bull; {new Date(t.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="w-7 h-7 bg-black/60 border border-[#141824] flex items-center justify-center text-xs font-bold text-cyan-400">
              {t.post_count || 0}
            </div>
          </Link>
        ))}
        {topics.length === 0 && <p className="text-xs text-zinc-500 tf-mono text-center py-2">No important topics.</p>}
      </div>
    </CardWrap>
  );
}


// --- Promoted Topics Widget ---
export function PromotedTopicsWidget({ title }) {
  const { data: topics = [] } = useQuery({ 
    queryKey: ["promotedTopics"], 
    queryFn: async () => (await api.get(`/idg/widgets/data/topics/promoted`)).data,
    staleTime: 120000 
  });
  
  return (
    <CardWrap title={title} icon={Star} testid="widget-promoted-topics">
      <div className="space-y-1.5">
        {topics.map(t => (
          <Link key={t.id} to={`/topic/${t.id}`} className="flex items-center gap-3 bg-[#06080d] p-2 border border-[#141824] hover:border-[var(--tf-primary)]/50 transition-colors">
            <Avatar user={t.author} size={36} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 text-amber-400" fill="currentColor" />
                <h5 className="text-xs font-bold text-white truncate">{t.title}</h5>
              </div>
              <div className="text-[10px] text-zinc-500 flex items-center gap-1 mt-0.5 truncate">
                <Username user={t.author} asText /> &bull; {new Date(t.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="w-7 h-7 bg-black/60 border border-[#141824] flex items-center justify-center text-xs font-bold text-cyan-400">
              {t.post_count || 0}
            </div>
          </Link>
        ))}
        {topics.length === 0 && <p className="text-xs text-zinc-500 tf-mono text-center py-2">No promoted topics.</p>}
      </div>
    </CardWrap>
  );
}
